// Furniture system data types and utilities

import { currentSeason, type Season } from '../domain/season';
import type { FurnitureCatalogResponse as FurnitureCatalog } from '../client';

// Furniture categories (should match backend categories)
export const FURNITURE_CATEGORIES = [
  "halloween",
  "walls",
  "floors",
  "beds",
  "rugs",
  "sofas",
  "nightstands",
  "storage",
  "fireplaces",
  "clocks",
  "decor",
  "gadgets",
  "windows",
  "paintings",
  "tables",
  "chairs",
  "ufo",
  "kitchen",
  "music",
] as const;

export type FurnitureCategory = typeof FURNITURE_CATEGORIES[number];

// Category translation keys (for UI) - function that returns translated category names
export const FURNITURE_CATEGORY_NAMES = (t: (key: string) => string): Record<FurnitureCategory, string> => ({
  // The pumpkin is part of the label on purpose (seasonal tab), kept out of the key.
  halloween: "🎃 " + t("Halloween"),
  walls: t("Walls"),
  // Floor category - where you can purchase new floor textures (e.g., wooden floor or tiles, etc.)
  floors: t("Floors"),
  beds: t("Beds"),
  rugs: t("Rugs"),
  sofas: t("Sofas"),
  nightstands: t("Nightstands"),
  storage: t("Large Wardrobes"),
  fireplaces: t("Fireplaces"),
  clocks: t("Grandfather Clocks"),
  decor: t("Decor"),
  // Phones and laptops (iPhone, MacBook)
  gadgets: t("Gadgets"),
  windows: t("Windows"),
  paintings: t("Paintings"),
  tables: t("Dining Tables"),
  chairs: t("Chairs"),
  ufo: t("UFO"),
  kitchen: t("Kitchen"),
  music: t("Musical Instruments"),
});

// ── Seasonal categories ──────────────────────────────────────────────────────
// A seasonal category (its tab and every item in it) only exists in the shop
// during its season (domain/season — the backend sells it only then too).
const SEASONAL_CATEGORY_SEASON: Partial<Record<FurnitureCategory, Season>> = {
  halloween: 'autumn',
};

export const isFurnitureCategoryInSeason = (category: string | undefined | null, now: Date = new Date()): boolean => {
  const season = SEASONAL_CATEGORY_SEASON[category as FurnitureCategory];
  return !season || season === currentSeason(now);
};

/** Shop tab order: Halloween first, then the rest alphabetically. */
export const compareFurnitureCategories = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a === 'halloween') return -1;
  if (b === 'halloween') return 1;
  return a.localeCompare(b);
};

/** Shop hint shown under the description of every musical instrument (one translated source). */
export const getMusicalInstrumentHint = (t: (key: string) => string): string =>
  t("Tap it in your house (outside build mode) to hear it play");

export const isMusicalInstrumentCatalog = (catalogId: number | undefined | null): boolean =>
  catalogId != null && catalogId in MUSICAL_INSTRUMENT_MELODIES;

/** Instruments play a melody when tapped outside build mode. */
export const MUSICAL_INSTRUMENT_MELODIES: Record<number, string> = {
  140: '/game-assets/sounds/piano-clair-de-lune.mp3',
  141: '/game-assets/sounds/piano-clair-de-lune.mp3',
  142: '/game-assets/sounds/piano-clair-de-lune.mp3',
  143: '/game-assets/sounds/piano-clair-de-lune.mp3',
  144: '/game-assets/sounds/organ-interstellar.mp3',
};

/**
 * Where an instrument's keyboard is in its (unmirrored) art, as fractions of the
 * sprite box: the line [x0, y0] → [x1, y1] the music notes rise from. The four
 * pianos share one layout.
 */
const PIANO_KEYS = [0.07, 0.44, 0.65, 0.64] as const;
export const MUSICAL_INSTRUMENT_KEYS: Record<number, readonly [number, number, number, number]> = {
  140: PIANO_KEYS,
  141: PIANO_KEYS,
  142: PIANO_KEYS,
  143: PIANO_KEYS,
  144: [0.22, 0.59, 0.52, 0.69],
};

// Utility functions
export const getFurnitureCategoryName = (category: string, t?: (key: string) => string): string => {
  if (!t) return category;
  const categoryNames = FURNITURE_CATEGORY_NAMES(t);
  return categoryNames[category as FurnitureCategory] || category;
};

/**
 * The scale the room art is shipped at (ROOM_ART_SCALE), part of every room-art
 * URL: the in-game size is computed from the picture size, so a picture cached at
 * another scale (the old 4x art) would show at the wrong size. A new scale → new URLs.
 */
const ROOM_ART_VERSION = '2x';

/** Where a catalogue item's picture lives, without the version. */
const furnitureArtPath = (furniture: FurnitureCatalog): string => {
  // - walls: /game-assets/room/walls/{id}.webp
  // - floors: /game-assets/room/floors/{id}.webp
  // - everything else: /game-assets/interior/{id}.webp
  if (furniture.category === 'walls') return `/game-assets/room/walls/${furniture.id}.webp`;
  if (furniture.category === 'floors') return `/game-assets/room/floors/${furniture.id}.webp`;
  return `/game-assets/interior/${furniture.id}.webp`;
};

export const getFurnitureImagePath = (furniture: FurnitureCatalog): string =>
  `${furnitureArtPath(furniture)}?v=${ROOM_ART_VERSION}`;

/**
 * Small preview of the same art for UI cards (shop, inventory, modals). The full
 * art is 2x the in-game size — never put it in an <img>: the shop alone decoded
 * ~500 MB that way and iPhones dropped the map's WebGL context.
 * Built by tools/atlas/build-room-art.mjs.
 */
export const getFurnitureThumbPath = (furniture: FurnitureCatalog): string => {
  const full = furnitureArtPath(furniture);
  const slash = full.lastIndexOf('/');
  return `${full.slice(0, slash)}/thumbs${full.slice(slash)}`;
};

export const getFurniturePrice = (furniture: FurnitureCatalog): { price: number | null; currency: string | null } => {
  return { price: furniture.price ?? null, currency: furniture.currency ?? null };
};

export const isFurnitureAvailable = (furniture: FurnitureCatalog): boolean => {
  return furniture.max_quantity == null || (furniture.max_quantity ?? 0) > 0;
};

/**
 * Retired furniture: still listed in the shop (so owners recognise it) but
 * marked "Sold out" and not purchasable. Set by max_quantity = 0 in the catalog;
 * the backend refuses the purchase on its own (sold_count >= max_quantity).
 */
export const isFurnitureSoldOut = (furniture: FurnitureCatalog): boolean => {
  return furniture.max_quantity === 0;
};

// Wall-mounted furniture categories (windows, paintings, wall decorations, etc.)
// These objects should be treated like windows - placed on walls, not on floor
export const WALL_MOUNTED_FURNITURE_CATEGORIES: ReadonlySet<string> = new Set([
  'windows',  // Windows
  'paintings', // Paintings and wall art
  // Add more wall-mounted categories here as needed
]);

// Check if a furniture item is wall-mounted (should be placed on walls like windows)
export const isWallMountedFurniture = (furniture: FurnitureCatalog | { category?: string } | null | undefined): boolean => {
  if (!furniture || !furniture.category) return false;
  return WALL_MOUNTED_FURNITURE_CATEGORIES.has(furniture.category);
};

// Room-decoration categories — replace room textures instead of placing on floor grid
export const ROOM_DECORATION_CATEGORIES = new Set(['walls', 'floors']);

export const isRoomDecoration = (furniture: FurnitureCatalog | { category?: string } | null | undefined): boolean => {
  if (!furniture || !furniture.category) return false;
  return ROOM_DECORATION_CATEGORIES.has(furniture.category);
};

// Get texture path for wall decoration item (single source, left wall is mirrored in render)
export const getWallTexturePath = (catalogId: number): string =>
  `/game-assets/room/walls/${catalogId}.webp?v=${ROOM_ART_VERSION}`;

export const getFloorTexturePath = (catalogId: number): string =>
  `/game-assets/room/floors/${catalogId}.webp?v=${ROOM_ART_VERSION}`;

// Furniture names (for UI)
// These functions accept a translation function `t` to support i18n
export const getFurnitureName = (id: number, t: (key: string) => string): string => {
  const names: Record<number, string> = {
    1: t("Ivory Serenity Bed"),
    2: t("Royal Turtle Carved Bed"),
    3: t("Sunset Paradise Bed"),
    4: t("Bamboo Breeze Nightstand"),
    5: t("Golden Weave Side Table"),
    6: t("Palmwood Carved Nightstand"),
    7: t("Heritage Turtle End Table"),
    8: t("Coral Coast Coffee Table"),
    9: t("Oceanview Bamboo Window"),
    10: t("Blossom Shore Window"),
    11: t("Carved Island View Window"),
    12: t("Turtle Crest Panorama Window"),
    13: t("Sunset Shell Window"),
    14: t("Azure Mandala Rug"),
    15: t("Oceanic Heritage Wardrobe"),
    16: t("Pearl Garland Cabinet"),
    17: t("Ivory Imperial Rug"),
    18: t("Cloud Comfort Rug"),
    19: t("Arctic Plush Rug"),
    20: t("Island Weave Wardrobe"),
    21: t("Silver Palm Haven Bed"),
    22: t("Palm Crown Wardrobe"),
    23: t("Blush Paradise Bed"),
    24: t("Carved Timber Cabinet"),
    25: t("Azure Breeze Bed"),
    26: t("Royal Candy Dream Bed"),
    27: t("Rosewood Scholar Table"),
    28: t("Rosewood Scholar Table"),
    29: t("Blush Horizon Window"),
    30: t("Royal Blush Dining Table"),
    31: t("Imperial Rose Wardrobe"),
    32: t("Golden Sunset Canvas"),
    33: t("Vintage Aristocat Portrait"),
    34: t("Ocean Crest Coffee Table"),
    35: t("Rustic Timber Table"),
    36: t("Bamboo Weave Table"),
    37: t("Palm Garland Table"),
    38: t("Carved Timber Worktable"),
    39: t("Portrait of the First Chancellor"),
    40: t("Arch of Public Vision"),
    41: t("Table of Civic Accord"),
    42: t("Ivory Chair of Mandate"),
    43: t("Crimson Seat of Authority"),
    44: t("Ceremonial State Carpet"),
    45: t("Hearth of Governance"),
    46: t("Statue of Founding Wisdom"),
    47: t("Fountain of Civic Renewal"),
    48: t("Prosperity Palm of the Republic"),
    49: t("Lamp of Deliberation"),
    50: t("Council Chamber Rug"),
    51: t("Ceremonial Iron Candelabra"),
    52: t("Clock of the Mayor's Term"),
    53: t("Archives of Policy"),
    54: t("Ceremonial Golden Loaf"),
    55: t("Classic Cream Wall"),
    56: t("Classic Cream Floor"),
    57: t("Marble Palace Walls"),
    58: t("Marble Palace Floor"),
    59: t("Bamboo Chair"),
    60: t("Tropical Garland Chair"),
    61: t("Rustic Wooden Chair"),
    62: t("Cloud Armchair"),
    66: t("I Love Web3Islands Poster"),
    67: t("Classic Wooden Chair"),
    68: t("Coral Hearth Fireplace"),
    69: t("Terracotta Palm Plant"),
    70: t("Lucky Panda Bamboo"),
    71: t("Red Rose Bush"),
    72: t("Blue Rose Bush"),
    73: t("Scholar's Hearth Fireplace"),
    74: t("I Want to Believe Poster"),
    75: t("Rose Royal Chair"),
    76: t("Cosmic Discovery Tapestry"),
    77: t("Imperial Sapphire Chair"),
    78: t("Nordic Marble Fireplace"),
    79: t("Reading Lounge Armchair"),
    80: t("Classic Chesterfield Sofa"),
    81: t("Vintage Leather Armchair"),
    82: t("Grand Corner Leather Sofa"),
    83: t("Green Comfort Armchair"),
    84: t("Emerald Living Sofa"),
    85: t("Coastal Relax Armchair"),
    86: t("Seaside Corner Sofa"),
    87: t("Cloud Plush Sofa"),
    88: t("Pelican Grandfather Clock"),
    89: t("Classic Sparrow Grandfather Clock"),
    90: t("Crimson Bullfinch Grandfather Clock"),
    91: t("Pink Orchid Plant"),
    92: t("MacBook Air Starlight"),
    93: t("Single Wax Candle"),
    94: t("Golden Candle Set"),
    95: t("Monstera Tropical Plant"),
    96: t("MacBook Air Mint"),
    97: t("Cactus Plant"),
    98: t("MacBook Air Midnight"),
    99: t("Pink Tulip Bouquet"),
    100: t("MacBook Air Silver"),
    101: t("Golden & Violet Tulip Bouquet"),
    102: t("Azure Rose Bouquet"),
    103: t("Crystal Candle Set"),
    104: t("Glass Rose Dome - Red"),
    105: t("Glass Rose Dome - Blue"),
    106: t("Stone Masonry Wall"),
    107: t("Bamboo Island Wall"),
    108: t("Soft Marble Plaster Wall"),
    109: t("Modern Panel Wall"),
    110: t("Dark Walnut Flooring"),
    111: t("Light Oak Flooring"),
    112: t("Cozy Pine Bed"),
    113: t("Tropical Oasis Chair"),
  114: t("Turquoise Palm Throne"),
    115: t("Jack-o'-Lantern Tower"),
    116: t("Bat-Winged Jack-o'-Lantern"),
    117: t("Midnight Pumpkin Bush"),
    118: t("Spider-Web Candy Basket"),
    119: t("Witching Hour Pumpkin Altar"),
    120: t("Dancing Skeleton"),
    121: t("Swamp Brew Cauldron"),
    122: t("Love Potion Cauldron"),
    123: t("Midnight Hex Cauldron"),
    124: t("Little Swamp Brew Cauldron"),
    125: t("Little Love Potion Cauldron"),
    126: t("Little Hex Cauldron"),
    127: t("Raven Wax Candles"),
    128: t("Pumpkin Glow Candles"),
    129: t("Trick-or-Treat Candy Jar"),
    130: t("Pastel Phantom Candy Jar"),
    131: t("Vanilla Cloud Latte"),
    132: t("Sunset Amber Tea"),
    133: t("Kyoto Garden Matcha"),
    134: t("Midnight Velvet Espresso"),
    135: "iPhone Duo Night Sky",
    136: "iPhone Duo Star White",
    137: "City of Sex Collection — Hearts",
    138: "City of Sex Collection — Leopard",
    139: "City of Sex Collection — Cow",
    140: t("Ivory Sonata Piano"),
    141: t("Onyx Nocturne Piano"),
    142: t("Mint Serenade Piano"),
    143: t("Rose Waltz Piano"),
    144: t("Gothic Cathedral Organ"),
};
  return names[id] || '';
};

// Furniture descriptions (for UI)
// These functions accept a translation function `t` to support i18n
export const getFurnitureDescription = (id: number, t: (key: string) => string): string => {
  const descriptions: Record<number, string> = {
    1: t("A refined minimalist bed wrapped in soft ivory tones and clean architectural lines. Designed for players who value quiet luxury, this piece instantly upgrades any bedroom into a premium retreat. Own it, and your home stops looking basic and starts feeling intentional."),
    2: t("An ornate handcrafted bed with intricate turtle carvings and deep island wood tones. This is not furniture — it's a status symbol. Perfect for players who want their home to radiate legacy, wealth, and authority."),
    3: t("A tropical dream bed adorned with seashell details, warm wood textures, and island garlands. It transforms any room into a permanent vacation. Guests won't just visit your house — they'll remember it."),
    4: t("Compact, stylish, and crafted in island bamboo style. A subtle detail that makes your bedroom feel complete. Players who understand aesthetics know: small upgrades create big impressions."),
    5: t("A light woven bamboo table with a refined tropical texture. It adds warmth and depth to any interior. Simple? Yes. Forgettable? Never."),
    6: t("Solid wood with engraved island motifs. Built for players who appreciate craftsmanship and presence. This piece quietly communicates that your home is curated, not random."),
    7: t("Deep carved patterns and iconic turtle emblems turn this table into a collectible statement. It feels rare because it is rare. Add it and elevate your interior from ordinary to elite."),
    8: t("Round wooden table decorated with shells and coral accents. It radiates relaxed island luxury. The kind of centerpiece that makes your space feel alive."),
    9: t("Wide bamboo-framed window with flowing white curtains and endless sea beyond. It doesn't just decorate your wall — it opens your world. Every great island home deserves a view like this."),
    10: t("Soft pink curtains, open breeze, and calming ocean scenery. This window adds romance and personality to your space. A perfect choice for players who want charm, not just furniture."),
    11: t("Elegant wood carvings frame a serene ocean horizon. A refined upgrade for those who want craftsmanship and atmosphere in one piece. Subtle luxury that speaks loudly."),
    12: t("Detailed turtle engravings and flowing curtains meet a peaceful sea view. This is premium island identity in one object. Install it, and your home instantly feels high-tier."),
    13: t("Shell details, coral carvings, and a golden sunset horizon. It captures the magic hour forever. Perfect for players who want their house to feel warm, alive, and unforgettable."),
    14: t("A richly patterned blue rug with symmetrical island motifs and premium texture. It grounds your room with elegance and depth. The difference between an empty floor and a designed interior."),
    15: t("Intricately carved doors with deep blue ocean panels and a central turtle emblem. This wardrobe commands attention. Built for players who want their interiors to feel powerful and prestigious."),
    16: t("A warm wooden cabinet decorated with soft island lights and coral carvings. Functional storage wrapped in personality. It adds life, not just utility."),
    17: t("A soft cream-toned rug with subtle ornamental patterns. Clean, elegant, and timeless. The kind of foundation piece that makes every other item look more expensive."),
    18: t("Ultra-soft circular fur rug that instantly adds coziness and luxury. It visually warms up any room. Once placed, your interior feels complete."),
    19: t("Thick fluffy rug with an organic silhouette. Designed to make your space feel exclusive and inviting. Comfort is a luxury — and this proves it."),
    20: t("Classic bamboo wardrobe with woven panels and sturdy island frame. Balanced, elegant, and essential for a cohesive tropical interior. Smart players invest in the pieces that define the room."),
    21: t("A refined bamboo bed with a cool silver-patterned blanket that balances tropical warmth with modern elegance. Perfect for players who want their island home to feel calm, mature, and intentionally designed."),
    22: t("A tropical statement wardrobe crowned with palm details and seashell garlands. It doesn't just store items — it frames your room with personality and island prestige."),
    23: t("Soft pink bedding paired with bamboo framing and shell garlands creates a dreamy island escape. Ideal for players who want charm, warmth, and a touch of romantic luxury."),
    24: t("Solid wood craftsmanship with engraved leaf accents. A grounded, reliable piece that adds weight and authenticity to your interior. Subtle power in furniture form."),
    25: t("A deep blue patterned blanket wrapped around carved tropical wood. This bed radiates coastal confidence. Clean, bold, and unmistakably premium."),
    26: t("A playful pink luxury bed with plush cushions and star accents. Designed for players who want their space to feel magical, expressive, and unforgettable."),
    27: t("Elegant pink carved table adorned with books and candles. A refined centerpiece for intellectual charm. It signals taste, culture, and thoughtful design."),
    28: t("A decorative pink carved table styled with literature and candlelight. Perfect for creating a refined, aristocratic atmosphere inside your island home."),
    29: t("A wide ocean-view window framed in soft pink with golden bows. Bright, uplifting, and luxurious — it instantly transforms a plain wall into a dreamy coastal panorama."),
    30: t("A grand pink table with ornate carved legs and golden accents. Built for players who want their interiors to feel noble, polished, and confidently elite."),
    31: t("An ornate pink wardrobe with mirrored doors and gold details. A true luxury piece that commands attention and elevates your entire room's status."),
    32: t("A vibrant painting of palms against a glowing island sunset. It captures that perfect golden-hour moment and brings warmth and emotion into your space."),
    33: t("A charming vintage-style portrait of elegant cats enjoying tea. Whimsical yet classy — ideal for players who appreciate personality and storytelling in their interiors."),
    34: t("A carved wooden table with deep blue ocean inlays and turtle motifs. This is heritage craftsmanship designed to impress. Your living space deserves a statement piece like this."),
    35: t("Solid wood construction with clean lines and strong legs. Reliable, versatile, and timeless — the kind of piece every well-designed home needs."),
    36: t("A woven bamboo tabletop with sturdy island framing. Light yet durable, it adds tropical authenticity and subtle texture to your interior."),
    37: t("A festive bamboo table decorated with shells and palm details. It radiates island celebration energy and instantly makes your space feel alive."),
    38: t("A solid wooden table with engraved accents beneath the surface. Functional yet expressive — perfect for players who value depth in design."),
    39: t("An intellectual presence overseeing every decision. This portrait represents wisdom, record-keeping, and strategic foresight. In a Mayor's Office, it signals that governance here is deliberate, documented, and respected."),
    40: t("A marble-arched window opening toward the horizon. Installed in the Mayor's Office, it symbolizes transparency and long-term vision. A leader must always see beyond the present moment."),
    41: t("Ivory marble crowned with a golden dove. This is where policies are drafted and alliances shaped. A Mayor without a proper negotiation table looks temporary. This one looks permanent."),
    42: t("The official seat of governance. Clean ivory carving with restrained gold detail reflects legitimacy without excess. Authority does not shout — it is recognized."),
    43: t("Deep red upholstery framed in ornate carving. This chair does not belong to decoration — it belongs to decision-makers. A statement that leadership here is firm and visible."),
    44: t("A rich crimson foundation beneath the Mayor's desk. Every official chamber requires grounding. This piece defines the center of power within the room."),
    45: t("A grand marble fireplace representing stability and continuity. Important reforms are imagined beside steady flames. It anchors the office in tradition and permanence."),
    46: t("A classical marble figure reminding visitors that governance is built on principles. It reinforces heritage, discipline, and respect for institutional order."),
    47: t("Flowing marble tiers symbolize renewal and responsible stewardship. A Mayor's Office adorned with movement and water conveys balance and controlled growth."),
    48: t("A cultivated palm in a marble vessel. Growth under structure. Prosperity under governance. It subtly communicates economic strength without words."),
    49: t("Ornate ivory lighting that casts a warm, strategic glow across official documents. It represents long nights, serious decisions, and responsibility carried properly."),
    50: t("Soft ivory grounding beneath the chamber's center. Authority should stand on refinement, not chaos. This completes the office visually and symbolically."),
    51: t("Five steady flames representing unity, continuity, and structured power. A darker accent suited for leaders who value gravity and symbolism."),
    52: t("A grand ivory clock marking every second of leadership. Time defines legacy. Installed here, it reminds both visitors and officials that authority is earned daily."),
    53: t("Leather-bound volumes and a golden quill symbolizing signed decrees and legislative progress. A Mayor's Office without documentation feels theatrical. This makes it institutional."),
    54: t("A polished golden loaf displayed as a symbol of prosperity and abundance. A subtle nod to how power sometimes measures success in tangible form. In this office, it stands as a reminder that leadership should protect wealth — not merely display it."),
    55: t("A clean cream-colored wall that works with almost any interior style. Simple, neutral, and reliable — the perfect base for building a beautiful room."),
    56: t("A smooth cream floor that keeps any interior feeling clean, open, and easy to decorate. The perfect neutral base that works with every style."),
    57: t("Luxurious marble walls inspired by the City Hall chambers. White stone with golden veins transforms any room into a palace of governance and prestige."),
    58: t("Polished marble floor tiles from the City Hall collection. Elegant checkered pattern that radiates authority and refined taste."),
    59: t("A clean bamboo chair with woven seating and natural island craftsmanship. Simple pieces like this quietly make a room feel authentic, warm, and thoughtfully furnished."),
    60: t("A festive bamboo armchair decorated with palms, shells, and colorful island garlands. It instantly turns any corner into a brighter, more playful spot that feels full of vacation energy."),
    61: t("A sturdy wooden chair with carved leaf details and strong handcrafted lines. It brings grounded rustic character to a room and makes even simple interiors feel more complete."),
    62: t("A plush oversized white armchair that looks almost too soft to be real. The kind of seat people notice immediately and secretly want for themselves the second they see it."),
    66: t("A clean pixel-style wall poster that proudly shows where your loyalty is. Small item, huge identity signal — perfect for players who want their room to feel personal, recognizable, and proudly part of the island culture. By purchasing this poster, you support your favorite game!"),
    67: t("A straightforward wooden chair with timeless proportions and strong rustic charm. Not flashy, but incredibly useful for making a home feel lived-in instead of empty."),
    68: t("A stone fireplace framed with driftwood, shells, coral, and tropical plants. It brings warmth, atmosphere, and that expensive seaside-luxury feeling that instantly upgrades the entire room."),
    69: t("A lush green palm in a classic clay pot that gives any room fresh tropical life. One plant like this makes furniture feel warmer, the space feel richer, and the whole interior feel more alive."),
    70: t("Tall decorative bamboo set in a panda planter with lucky charm accents. Cute at first glance, but also the kind of piece that adds character, humor, and a collectible feel to your interior."),
    71: t("A dense blooming rose bush packed with rich red petals and dramatic color. Romantic, bold, and impossible to overlook — perfect when you want one item to make the whole room feel more alive."),
    72: t("A rare blue rose arrangement that feels elegant, unusual, and premium the moment you place it. The kind of decor that quietly tells visitors your taste is not basic."),
    73: t("A cozy brick fireplace styled with books, candles, and coffee mugs for the perfect slow-evening atmosphere. It sells comfort immediately: one glance and players can already imagine staying there longer than planned."),
    74: t("An iconic framed poster with a UFO sighting and just enough mystery to pull attention from across the room. It adds personality fast and makes the space feel like it belongs to someone interesting."),
    75: t("A refined pink chair with delicate gold detailing and aristocratic curves. Decorative, elegant, and charming enough to make a room feel softer, richer, and more curated."),
    76: t("A mesmerizing celestial artwork filled with stars, suns, and symbolic wonder. This is the kind of wall piece that makes a room feel deeper, smarter, and far less ordinary."),
    77: t("An ornate carved chair with deep blue panels and regal gold-like detailing. It doesn't just fill space — it makes the room feel more important the second it appears."),
    78: t("A bright stone fireplace with glass doors, candles, books, and clean premium styling. It creates that high-end cozy look players always want: warm, polished, and undeniably expensive-feeling."),
    79: t("A soft cream armchair with a cozy blanket and a book waiting on the seat. The kind of furniture that quietly invites someone to sit down, relax, and stay longer than they planned."),
    80: t("A luxurious tufted sofa with deep cushions and timeless elegance. Rich, comfortable, and unmistakably premium — exactly the kind of centerpiece that makes a room feel expensive."),
    81: t("A dark leather armchair with deep cushions and a worn-in charm. It carries the feeling of long conversations, late evenings, and rooms filled with character."),
    82: t("A massive corner leather sofa built for gatherings. Deep seats, strong structure, and plenty of space make it the natural heart of any living room."),
    83: t("A soft green armchair with a relaxed modern feel. Simple, inviting, and perfect for bringing a little warmth and color into an interior."),
    84: t("A vibrant green sofa with patterned pillows that instantly brightens a room. Comfortable, lively, and perfect for interiors that refuse to look boring."),
    85: t("A light armchair with blue accents and relaxed seaside styling. It feels calm, breezy, and effortlessly comfortable — like furniture that belongs near the ocean."),
    86: t("A spacious L-shaped sofa with warm fabrics and coastal tones. Designed for long evenings, conversations, and rooms that feel welcoming the moment you walk in."),
    87: t("An ultra-soft white sofa piled with pillows that looks impossibly comfortable. One glance and visitors already imagine sinking into it."),
    88: t("A charming wooden grandfather clock with a pelican cuckoo detail. Elegant, playful, and impossible to ignore when it announces the passing of time."),
    89: t("A dark wood grandfather clock with a small sparrow peeking from its door. Traditional craftsmanship mixed with a little personality."),
    90: t("A striking red grandfather clock featuring a bright bullfinch cuckoo. Bold, decorative, and the kind of piece that instantly draws attention."),
    91: t("An elegant pink orchid placed in a geometric ceramic pot. Delicate, stylish, and perfect for interiors that need a touch of refined beauty."),
    92: t("The latest MacBook Air in Starlight finish — a warm, elegant gold that feels both premium and approachable. This is the laptop that makes any workspace look instantly more sophisticated."),
    93: t("A small wax candle with a warm gentle flame. Simple, calm, and perfect for creating a cozy atmosphere in the room."),
    94: t("A set of elegant candles with soft glowing flames. Their warm light instantly makes any interior feel calmer and more welcoming."),
    95: t("A lush monstera plant in a decorative pot with carved patterns. Bold leaves bring fresh tropical life into the space."),
    96: t("The latest MacBook Air in Mint finish — a fresh, vibrant green that brings energy and creativity to any desk. Perfect for players who want their workspace to feel modern and alive."),
    97: t("A tall cactus growing in a minimalist ceramic pot. Compact, calm, and perfect for interiors that like a little desert charm."),
    98: t("The latest MacBook Air in Midnight finish — a deep, rich blue-black that looks professional and powerful. This is the laptop that commands respect in any interior."),
    99: t("A delicate bouquet of pink and white tulips arranged in a crystal vase. Soft colors that instantly brighten a room."),
    100: t("The latest MacBook Air in Silver finish — the classic, timeless color that never goes out of style. Clean, minimalist, and perfect for any workspace."),
    101: t("A vibrant bouquet of purple and golden tulips. Rich colors bring energy and personality into the room."),
    102: t("A striking bouquet of deep blue roses in a glass vase. Rare color and elegant shape make it a memorable decoration."),
    103: t("A set of elegant blue candles glowing with calm light. Their soft color adds a peaceful atmosphere to any interior."),
    104: t("A single red rose protected under a glass dome. Romantic, dramatic, and impossible to ignore."),
    105: t("A rare blue rose displayed beneath a crystal dome. A mysterious and elegant decorative centerpiece."),
    106: t("A textured wall built from layered natural stone. It adds structure, weight, and a feeling of solid craftsmanship to any room."),
    107: t("Vertical bamboo panels that instantly bring tropical island energy into the space. Light, natural, and perfect for relaxed seaside interiors."),
    108: t("A smooth decorative plaster wall with subtle marble textures. Elegant, soft, and ideal for interiors that aim to feel refined without looking cold."),
    109: t("Minimal vertical wall panels that create a clean contemporary look. Simple lines that make furniture and decorations stand out even more."),
    110: t("Rich dark walnut planks with deep natural texture. This floor gives any interior a heavier, more luxurious atmosphere the moment it's placed."),
    111: t("A clean light oak floor that instantly makes interiors feel brighter and more spacious. Perfect for rooms that should look warm, modern, and inviting."),
    112: t("A rustic wooden bed made from pine, perfect for a warm and inviting bedroom."),
    113: t("A vibrant chair with a turquoise seat and tropical leaf patterns, supported by sturdy bamboo framing. This cheerful piece effortlessly adds a splash of island spirit to your room, inviting relaxation and warmth."),
  114: t("A lavish armchair of carved gold and vivid turquoise, its backrest embroidered with a tropical palm. This seat commands attention with opulent craftsmanship, turning any room into a mayoral chamber of island prestige."),
    115: t("Three grinning pumpkins stacked into a glowing tower, wrapped in vines and guarded by bat wings on top. The kind of Halloween centerpiece that tells every visitor this house takes spooky season seriously."),
    116: t("A cheeky jack-o'-lantern with leathery bat wings and a wicked candlelit grin. Small enough to fit any corner, loud enough to make the whole room feel like October."),
    117: t("A dark, dense bush bursting with tiny glowing jack-o'-lanterns, planted in a wrought-iron cauldron pot. Mysterious, elegant and a little bit cursed — exactly how a Halloween garden should feel."),
    118: t("A woven basket overflowing with candy corn, lollipops and wrapped sweets, tangled in cobwebs with a few spiders keeping watch. Trick or treat? In this house, definitely treat."),
    119: t("A small ritual stage of carved pumpkins, a witch's broom, a bubbling cauldron and an ancient scroll. Place it in the room and the whole house suddenly feels like it has secrets."),
    120: t("A cheerful skeleton in a witch's hat, dancing on its stand with a candy bucket in hand. Spooky, silly and impossible not to smile at — the life of every Halloween party."),
    121: t("A heavy iron cauldron bubbling with glowing swamp-green brew and floating bones. Nobody knows what's cooking, and that's exactly the point."),
    122: t("A bright pink cauldron simmering with a sweet, sparkling love potion. Romantic, mischievous and slightly dangerous — one sip and nobody leaves your house."),
    123: t("A pitch-black cauldron etched with a bat, its brew glowing with eerie colours. Pure dark magic for players who like their Halloween with a serious edge."),
    124: t("A tiny swamp-brew cauldron that fits right on a table or shelf. All the bubbling witchcraft of the big one, just small enough to keep within reach."),
    125: t("A miniature pink potion cauldron for desks, shelves and nightstands. A cute little dose of magic that makes any surface feel enchanted."),
    126: t("A miniature black cauldron with a softly glowing brew. A small but sinister detail that turns an ordinary shelf into a witch's workspace."),
    127: t("A cluster of black wax candles dripping in the dark, with tiny embers floating above. Moody, gothic and perfect for a slow, spooky evening."),
    128: t("A trio of bright orange candles melting into warm pumpkin-coloured drips. They bring the cozy side of Halloween straight to your table."),
    129: t("A glass jar with a bat on the lid, stuffed with ghost candies, mini pumpkins and candy corn. A sweet little trophy of the scariest night of the year."),
    130: t("A pastel candy jar filled with mint ghosts, pink pumpkins and tiny witch hats. Spooky-cute and soft enough for even the gentlest island homes."),
    131: t("A tall glass of vanilla latte crowned with whipped cream, cinnamon and caramel drops. Warm, sweet and slow — the drink of lazy mornings and long conversations."),
    132: t("Clear amber tea with slices of orange and a hint of spice, served on a glass saucer. It smells like a calm evening and tastes like a deep breath."),
    133: t("A layered iced matcha latte with silky foam and a little leaf drawn on top. Fresh, green and quietly stylish — the drink of people who know what they like."),
    134: t("A double-walled glass of dark espresso with a heart drawn in the crema. Small, strong and serious — exactly what a busy island owner runs on."),
    135: t("Apple's first foldable iPhone in Night Sky — a deep blue that opens like a book into a 7.6-inch display. Titanium frame, A20 Pro, a nearly invisible crease. An exclusive piece for players who always have the newest thing first."),
    136: t("Apple's first foldable iPhone in Star White — a pearly finish that catches every light, folding open into a 7.6-inch display. Titanium, A20 Pro, and the kind of desk detail everyone notices."),
    137: t("The softest bed of the City of Sex Collection: a cream tufted headboard, white iron frame and a blanket covered in little red hearts. It looks innocent — the collection name says otherwise."),
    138: t("The boldest bed of the City of Sex Collection: dark mahogany, a tufted leather headboard and a wild leopard-print blanket. Not a bed for shy people."),
    139: t("The most unforgettable bed of the City of Sex Collection: white iron lace, plush pillows and an absurd cow-print blanket. Guests laugh, take a screenshot, then secretly want one."),
    140: t("A grand ivory piano with carved panels and an open lid. The room instantly feels like a concert salon."),
    141: t("A gleaming black piano with gold pedals and an open lid, made for late-night nocturnes and quiet elegance."),
    142: t("A pastel mint piano with ornate carvings — playful, fresh and elegant."),
    143: t("A rose-pink piano that looks like it came from a fairy-tale ballroom — soft, romantic and charming."),
    144: t("A towering gothic pipe organ with carved spires and three keyboards. Its sound could fill a cathedral — or your whole house."),
};
  return descriptions[id] || '';
};